import React, { memo, useMemo, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDealerPrice } from "./useDealerPrice";
import { useDealerFavorite } from "./useDealerFavorite";
import { DealerCardContainer } from "./DealerCardContainer";
import { 
  DealerCardContent,
  DealerCardSidebar
} from "@/components/dealer-card";
import { DealerReportModal } from "@/components/dealer-reports/DealerReportModal";
import { useStore } from "@/store/useStore";
import { DEFAULT_GOLD_PURITY, DEFAULT_SILVER_PURITY } from "@/lib/constants";
import { formatPurity } from "@/lib/formatPurity";
import { getPurityMultiplier } from "@/lib/pricing";

interface DealerCardProps {
  id: string;
  name: string;
  address: string;
  city?: string;
  phone?: string;
  openingHours?: string;
  rating: number;
  rating_count?: number;
  is_verified: boolean; 
  activated?: boolean;
  isFavorite: boolean;
  distance?: string;
  buySpread?: number;
  className?: string;
  image_url?: string;
  postal_code?: string;
  email?: string;
  onActivateClick?: () => void;
}

export const DealerCard = memo(function DealerCard({ 
  id, 
  name, 
  address, 
  city,
  phone,
  openingHours,
  rating, 
  rating_count = 0,
  is_verified, 
  activated = false,
  isFavorite: initialIsFavorite,
  distance,
  buySpread,
  className = "",
  image_url,
  postal_code,
  email,
  onActivateClick
}: DealerCardProps) {
  // State for report modal
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  
  // Get favorites from store
  const { favoritesDealers, fetchFavoriteDealers } = useStore();
  
  // Use the dealer favorite hook
  const { handleToggleFavorite, isFavorite } = useDealerFavorite(id);
  
  // Get necessary data from price store
  const { currentGoldPrice: rawGoldPrice, goldPurity, silverPurity } = useStore();
  
  // Memoize effective purities
  const effectiveGoldPurity = useMemo(() => {
    return goldPurity || DEFAULT_GOLD_PURITY;
  }, [goldPurity]);
  
  const effectiveSilverPurity = useMemo(() => {
    return silverPurity || DEFAULT_SILVER_PURITY;
  }, [silverPurity]);
  
  // Fetch favorites when component mounts
  useEffect(() => {
    fetchFavoriteDealers();
  }, [fetchFavoriteDealers]);
  
  // Debug log for favorite state
  useEffect(() => {
    console.log(`DealerCard ${name} - Favorite status:`, {
      isFavorite, 
      initialIsFavorite,
      inFavoritesArray: favoritesDealers.includes(id),
      dealerId: id
    });
  }, [isFavorite, initialIsFavorite, id, name, favoritesDealers]);
  
  // Memoize purity multiplier calculation
  const purityMultiplier = useMemo(() => {
    return getPurityMultiplier("ORO", effectiveGoldPurity as any);
  }, [effectiveGoldPurity]);
  
  // Memoize adjusted market price calculation
  const adjustedMarketPrice = useMemo(() => {
    const baseMarketPrice = rawGoldPrice?.price || 0;
    return baseMarketPrice * purityMultiplier;
  }, [rawGoldPrice, purityMultiplier]);
  
  // Use dealer price hook with the adjusted market price
  const { dealerBuyPrice } = useDealerPrice(rawGoldPrice, effectiveGoldPurity, buySpread);
  
  // Add debug logs
  console.log(`DealerCard ${name} OPTIMIZED - buySpread=${buySpread}, marketPrice=${adjustedMarketPrice}, dealerBuyPrice=${dealerBuyPrice}, purity=${String(effectiveGoldPurity)}, multiplier=${purityMultiplier}`);
  
  // Memoize layout flags
  const { isHorizontalLayout, isHomePage } = useMemo(() => ({
    isHorizontalLayout: className.includes('md:flex-row'),
    isHomePage: className.includes('card-metal-highlight')
  }), [className]);

  // Memoize formatted purity for display
  const formattedPurity = useMemo(() => {
    return formatPurity("gold", String(effectiveGoldPurity));
  }, [effectiveGoldPurity]);

  // Handle report modal
  const handleReportClick = () => {
    setIsReportModalOpen(true);
  };

  return (
    <DealerCardContainer 
      id={id}
      city={city}
      className={`${className} h-full`}
      activated={activated}
      isHomePage={isHomePage}
      isHorizontalLayout={isHorizontalLayout}
      isModalOpen={isReportModalOpen}
    >
      <DealerCardSidebar
        name={name}
        address={address}
        is_verified={is_verified}
        city={city}
        postal_code={postal_code}
        phone={phone}
        openingHours={openingHours}
        isFavorite={isFavorite}
        isHomePage={isHomePage}
        isHorizontalLayout={isHorizontalLayout}
        onToggleFavorite={handleToggleFavorite}
      />
      
      <DealerCardContent
        marketPrice={adjustedMarketPrice}
        dealerBuyPrice={dealerBuyPrice}
        buySpread={buySpread}
        rating={rating}
        rating_count={rating_count}
        email={email}
        distance={distance}
        is_verified={is_verified}
        activated={activated}
        dealerId={id}
        city={city}
        dealerName={name}
        dealerAddress={address}
        onActivateClick={onActivateClick}
        onReportClick={handleReportClick}
        isHomePage={isHomePage}
        isHorizontalLayout={isHorizontalLayout}
        metalPurity={formattedPurity}
        metal="gold"
      />
      
      {/* Portal for Report Modal */}
      {isReportModalOpen && typeof document !== 'undefined' && createPortal(
        <DealerReportModal
          isOpen={isReportModalOpen}
          onOpenChange={setIsReportModalOpen}
          dealerId={id}
          dealerName={name}
          dealerAddress={address}
        />,
        document.body
      )}
    </DealerCardContainer>
  );
});
